import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';

import { type IPhoneCall } from '../../../extViewmodels';
import { queryKeys } from './queryKeys';

export type AttachCallToLeadVariables = {
	phoneCallId: string;
	companyId: string;
};

export type AttachCallToLeadResult = {
	phoneCallId: string;
	companyId: string;
};

/**
 * Client-side "attach this active call to a lead" mutation, used by the
 * CallFromUnknown rescue flow. The legacy queue surfaces the same flow but
 * has no dedicated server endpoint either — it relies on the wrap-up's
 * `/phonecall/{id}/outcome` POST to associate the call once the rep saves.
 * Until then, the association is purely in the rep's UI state (legacy: the
 * MobX `phoneCalls.currentCall.companyId`; here: react-query cache + the
 * `currentCompanyId` URL param).
 *
 * This hook centralises that transition:
 *   1. Optimistically patches the cached `IPhoneCall` so any consumer
 *      reading `usePhoneCallById(id).data?.companyId` sees the new link.
 *   2. Invalidates the lead + contacts queries for the target company so
 *      the focused view re-fetches against the rescued lead.
 *
 * Wrapped in `useMutation` so the call site can read `isPending` / `error`
 * via the standard react-query API and the rescue UI can show a spinner
 * during the brief invalidation window.
 */
export function useAttachCallToLead(): UseMutationResult<AttachCallToLeadResult, Error, AttachCallToLeadVariables> {
	const queryClient = useQueryClient();

	return useMutation<AttachCallToLeadResult, Error, AttachCallToLeadVariables>({
		mutationFn: async ({ phoneCallId, companyId }) => {
			// No server hop today — see header comment. We patch the cached
			// phone-call optimistically so downstream consumers see the link
			// without a refetch. If/when the backend adds a dedicated
			// `phonecall/{id}/attach` endpoint, this is the place to wire it
			// (and `onError` should roll the optimistic patch back).
			queryClient.setQueryData<IPhoneCall | undefined>(queryKeys.focusedView.phoneCall(phoneCallId), prev =>
				prev ? { ...prev, companyId } : prev
			);
			return { phoneCallId, companyId };
		},
		onSuccess: (_result, variables) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.lead(variables.companyId) });
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.contacts(variables.companyId) });
		},
	});
}
